import { apiClient } from '@/api/client';

interface BaseNotification {
  id: string;
  readAt: string | null;
  createdAt: string;
}

/** A profile-view alert (mirrors backend NotificationView). */
export interface ProfileViewNotification extends BaseNotification {
  type: 'profile_viewed';
  recipientEmail: string | null;
  recipientName: string | null;
  viewedAt: string | null;
  isRepeatView: boolean;
}

/** A team-member-joined alert (mirrors backend NotificationView, MYN-56). */
export interface MemberJoinedNotification extends BaseNotification {
  type: 'member_joined';
  newMemberName: string | null;
  inviteId: string | null;
  linkPath: string | null;
}

/** A "someone wants to join" alert (mirrors backend NotificationView). */
export interface JoinRequestedNotification extends BaseNotification {
  type: 'join_requested';
  joinRequesterName: string | null;
  joinRequesterEmail: string | null;
  linkPath: string | null;
}

/**
 * The outcome of an MNR admin's review of a Trusted Partner offer (MYN-106
 * AC8). Both outcomes share one shape - they differ only in the `type`
 * discriminant and the copy keyed off it, not in the fields they carry.
 *
 * `offerTitle` comes from a join on the Backend rather than the notification
 * row itself, so it is null for an offer since deleted; the copy falls back
 * to "your offer" in that case rather than rendering an empty quote.
 */
export interface OfferReviewedNotification extends BaseNotification {
  type: 'offer_accepted' | 'offer_rejected';
  offerId: string | null;
  offerTitle: string | null;
  linkPath: string | null;
}

/**
 * An MNR admin switched on SR NEXT Premium for this startup (v1.2, FR-145).
 * Visible to members holding `billing.manage`; links to Team Access.
 */
export interface DataRoomPremiumGrantedNotification extends BaseNotification {
  type: 'data_room_premium_granted';
  /** When the Premium period ends (ISO); null only on a defensive missing value. */
  accessEndsAt: string | null;
  linkPath: string | null;
}

/**
 * SR NEXT Premium is about to end, or has ended (v1.2, FR-137, MYN-259):
 * 30 and 7 days before the end, and 1 day after it. Visible to members
 * holding `billing.manage`; links to Billing & Info.
 */
export interface DataRoomPremiumReminderNotification extends BaseNotification {
  type: 'data_room_premium_expiring' | 'data_room_premium_expired';
  /** When the Premium period ends or ended (ISO). */
  accessEndsAt: string | null;
  linkPath: string | null;
}

export type AppNotification =
  | ProfileViewNotification
  | MemberJoinedNotification
  | JoinRequestedNotification
  | OfferReviewedNotification
  | DataRoomPremiumGrantedNotification
  | DataRoomPremiumReminderNotification;

/** List the caller's own entity's notifications, newest first (MYN-55). Polled, not pushed. */
export async function listNotifications(): Promise<AppNotification[]> {
  const { data } = await apiClient.get<AppNotification[]>('/notifications');
  return data;
}

/** Mark one notification read. */
export async function markNotificationRead(id: string): Promise<void> {
  await apiClient.patch(`/notifications/${id}/read`);
}

/** Mark every notification for the caller's own entity read at once. */
export async function markAllNotificationsRead(): Promise<void> {
  await apiClient.post('/notifications/read-all');
}
